import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import i18next from 'i18next';
import { ChevronDownIcon, CloseIcon, ProviderConsumer, Text } from '@fluentui/react-northstar';
import { CategoryOption, listWidthRows, matchingOptions, optionName, resolveOption } from './inlineCategoryOptions';
import { ListSize, TypeaheadList, revealRow, useListPlacement } from './TypeaheadList';
import { FieldErrorText, RequiredAsterisk } from '../FieldErrorText';

/** A field's box height for its type size - the ticket page's 32px at 14px, a step smaller at 12px. */
const boxHeight = (size: ListSize) => (size === 12 ? 28 : 32);

/** A phone or tablet: no hover, a finger for a pointer. */
const isTouchScreen = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(hover: none) and (pointer: coarse)').matches;

interface FieldProps {
  label: string;
  options: CategoryOption[];
  valueId: number | null;
  onChange: (id: number | null) => void;
  /**
   * Its label carries the ticket page's red asterisk, and while blank "Required field must be filled out." shows under
   * it, as on the ticket page - the panel keeps Save off meanwhile (Shaun, 2026-10-20).
   */
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  /** Box and list type size; the label is always the ticket page's 14px. */
  size?: ListSize;
  /**
   * An X in the box while it holds a value, as the ticket page's Team, Group and Assignee have; it empties the
   * field (the panel applies the knock-on resets, as the page does).
   */
  clearable?: boolean;
  /** Greyed and not editable, as the ticket page's fields are on a closed, merged or deflected ticket. */
  disabled?: boolean;
  /** Drawn red, whatever the value - a form's own validation (the ticket page's error state). */
  error?: boolean;
  /**
   * False on a form (TypeaheadPicker): a value is taken only when it is picked - Enter, Tab or a click - or when the
   * box is left holding an option's exact name, never while typing. A form acts on each change at once (a Status
   * change opens the resolution dialog), so typing "Resolved" must not choose it halfway through. The list's panels
   * keep taking a value as soon as the typing names one; nothing happens there until Save.
   */
  liveChange?: boolean;
  /** Leaves the label out - for a caller that draws its own (TypeaheadPicker, with the required star). */
  hideLabel?: boolean;
  /** The list as wide as the box and in line with it - a form's dropdowns (Edit, Add, Bulk Edit), as production's are. */
  listFitsBox?: boolean;
}

/**
 * The X or chevron at a box's right-hand end, as the ticket page's dropdowns have. Out of the Tab order (the box
 * itself is the stop), and pressing it does not take focus out of the box.
 */
const BoxIcon = ({ label, color, onClick, children, top }: { label: string; color: string; onClick: () => void; children: React.ReactNode; top?: number }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    tabIndex={-1}
    onMouseDown={event => event.preventDefault()}
    onClick={onClick}
    style={{
      // Centred on the box, or - a box of chips that wraps - on its first line, top right, as the ticket page's stays.
      position: 'absolute', right: 6, top: top ?? '50%', transform: 'translateY(-50%)',
      display: 'flex', flexShrink: 0, border: 0, padding: 2, background: 'transparent', cursor: 'pointer', color
    }}
  >
    {children}
  </button>
);

/** The best match as the agent types: what the text resolves to, else the top of the list. */
const bestMatch = (options: CategoryOption[], typed: string, resolved: number | null) => {
  const matches = matchingOptions(options, typed);
  if (!typed.trim() || !matches.length) return -1;
  return Math.max(matches.findIndex(option => option.id === resolved), 0);
};

/**
 * One typable box inside an edit panel - the ticket list cells' typing, outside a cell. Typing that names an
 * option sets it; the best match is lit, and Enter or a click takes it. Escape with the list showing closes the
 * list; with it shut, Escape and Enter are left to the panel (cancel, save). Tab takes the lit option and
 * moves on. Blank clears the field unless it is required.
 */
export const TypeaheadField = ({ label, options, valueId, onChange, required, autoFocus, placeholder, size = 14, clearable, disabled, error, liveChange = true, hideLabel, listFitsBox }: FieldProps) => {
  const [text, setText] = useState(optionName(options, valueId));
  const [filter, setFilter] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  /** The mouseup of the click that focused the box would undo the selection made on focus. */
  const keepSelectionRef = useRef(false);
  /** The agent is typing in the box - from a keystroke until a pick, Escape or leaving the box. */
  const [typing, setTyping] = useState(false);
  /**
   * On a touch screen a tap in the box is for typing, as in production: the keyboard comes up with the cursor at the
   * end and no list opens - the list covered by the keyboard was the annoyance (Shaun, 2026-10-21). The down arrow
   * opens the list with the keyboard held down (inputMode none) until the box itself is tapped.
   */
  const touch = useRef(isTouchScreen()).current;
  const [keyboardHeld, setKeyboardHeld] = useState(false);
  /** Set while the box is blurred and focused again to bring the keyboard up, so that blur is not a leave. */
  const refocusingRef = useRef(false);
  /** On a phone every picker is a plain dropdown - tap to open, tap to pick, no typing and so no keyboard (Shaun, 2026-10-22). */
  const pickOnly = touch;
  /** The tap that entered the box: it selects the whole value, so typing replaces it; later taps place the cursor. */
  const enteredByTapRef = useRef(false);
  /**
   * A value too long for the box shows its end whenever the agent is not typing - "...Standard > Tier 6 > Tier 7", the
   * part that was chosen, as the list's rows do, and at once when it is picked (Shaun, 2026-10-19). Typing shows the
   * text from its start.
   */
  const [showEnd, setShowEnd] = useState(false);
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (typing || !input) { setShowEnd(false); return; }
    setShowEnd(input.scrollWidth > input.clientWidth + 1);
  }, [text, typing]);
  const shown = matchingOptions(options, filter);
  // As wide as what it shows: the tree's names while nothing is typed, the full paths once something is.
  const widthRows = listWidthRows(options, filter);
  const position = useListPlacement(inputRef, listRef, listOpen, widthRows.map(row => row.text), size, listFitsBox, widthRows.map(row => row.indent));

  // A value set from outside (the cascade from Team or Group) shows at once.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setText(optionName(options, valueId));
  }, [valueId, options]);

  /**
   * The last value this box reported and the form has not yet taken (undefined: none). Leaving the box does not
   * report it again - a form that holds a pick back, or turns it down, would otherwise be told twice.
   */
  const reportedRef = useRef<number | null | undefined>(undefined);
  useEffect(() => { reportedRef.current = undefined; }, [valueId]);
  const report = (id: number | null) => {
    reportedRef.current = id;
    onChange(id);
  };

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (active >= 0) revealRow(listRef.current, active);
  }, [active]);

  const pick = (option: CategoryOption) => {
    setTyping(false);
    setText(option.name);
    setFilter('');
    setListOpen(false);
    setActive(-1);
    report(option.id);
    // On a phone the pick puts the keyboard away: while it is up Safari keeps the page slid up and the top of the form
    // out of reach (Shaun, 2026-10-21). A tick later, so leaving the box sees the picked value rather than the typing.
    if (touch) setTimeout(() => inputRef.current?.blur(), 0);
  };

  const clear = () => {
    setTyping(false);
    setText('');
    setFilter('');
    setListOpen(false);
    setActive(-1);
    report(null);
  };

  /** Opens the whole list with the current value lit. */
  const openList = () => {
    setFilter('');
    setListOpen(true);
    setActive(options.findIndex(option => option.id === valueId));
  };

  /** The chevron: opens the list (on the current value) or closes it. */
  const toggleList = () => {
    if (listOpen) { setListOpen(false); setActive(-1); return; }
    const input = inputRef.current;
    if (pickOnly) {
      if (document.activeElement !== input) input?.focus();
      openList();
      return;
    }
    if (touch && input) {
      // The keyboard goes down whenever the arrow opens the list, typing or not: a box already focused is blurred and
      // focused again with no keyboard asked for.
      input.inputMode = 'none';
      setKeyboardHeld(true);
      refocusingRef.current = true;
      if (document.activeElement === input) input.blur();
      input.focus();
      refocusingRef.current = false;
    } else if (document.activeElement !== input) {
      input?.focus();
    }
    openList();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (!listOpen) { openList(); return; }
      if (!shown.length) return;
      setActive(prev => (prev + (event.key === 'ArrowDown' ? 1 : -1) + shown.length) % shown.length);
      return;
    }
    if (event.key === 'Enter' && listOpen && active >= 0 && shown[active]) {
      event.preventDefault();
      event.stopPropagation();
      pick(shown[active]);
      return;
    }
    if (event.key === 'Escape' && listOpen) {
      event.preventDefault();
      event.stopPropagation();
      setListOpen(false);
      setActive(-1);
      setTyping(false);
      return;
    }
    if (event.key === 'Tab' && listOpen && active >= 0 && shown[active]) pick(shown[active]);
  };

  return (
    <ProviderConsumer
      render={globalTheme => {
        const { colorScheme } = globalTheme.siteVariables;
        // No red box for a blank required field - the ticket page's asterisk and message say it (Shaun, 2026-10-19 on the
        // page, 2026-10-20 in the list's panels). A panel still boxes typing that names nothing, which it would not
        // take; a form (liveChange off) puts that text back on leaving, so only the form's own error is boxed there.
        const invalid = liveChange
          ? text.trim() !== '' && resolveOption(options, text) == null && text !== optionName(options, valueId)
          : !!error;
        const iconColor = disabled ? colorScheme.default.foregroundDisabled1 : colorScheme.default.foreground;
        const trailingIcon = () => {
          if (disabled) {
            return (
              <span aria-hidden style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', color: iconColor }}>
                <ChevronDownIcon size="small" />
              </span>
            );
          }
          // A field with a value shows the X in the arrow's place, on a phone too - there a tap on the box opens the
          // list (Shaun, 2026-10-22).
          if (clearable && valueId != null) {
            return (
              <BoxIcon
                label={`${i18next.t('time-tracking.clear')} ${label}`}
                color={iconColor}
                onClick={() => {
                  clear();
                  if (!touch) inputRef.current?.focus();
                }}
              >
                <CloseIcon size="small" />
              </BoxIcon>
            );
          }
          return (
            <BoxIcon label={label} color={colorScheme.default.foreground} onClick={toggleList}>
              <ChevronDownIcon size="small" />
            </BoxIcon>
          );
        };
        return (
          <div>
            {!hideLabel && <Text style={{ fontSize: 14 }}>{label}{required && <RequiredAsterisk />}</Text>}
            <div className="relative">
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-label={label}
              aria-expanded={listOpen}
              aria-autocomplete="list"
              aria-invalid={invalid}
              autoComplete="off"
              spellCheck={false}
              placeholder={placeholder}
              // Marks around the text keep a bracket at either end in place in the right-to-left box (showEnd).
              value={showEnd ? `\u200E${text}\u200E` : text}
              dir={showEnd ? 'rtl' : undefined}
              // Entering the box selects what is in it, so typing replaces it. The list opens on a click, typing or the
              // arrow keys - not on Tab, so tabbing through a panel does not open every list on the way to Save
              // (Shaun, 2026-10-13).
              inputMode={touch && (keyboardHeld || pickOnly) ? 'none' : undefined}
              readOnly={pickOnly}
              onFocus={event => {
                if (touch) {
                  if (!refocusingRef.current) { enteredByTapRef.current = true; }
                  return;
                }
                event.target.select();
                keepSelectionRef.current = true;
              }}
              onMouseUp={event => {
                if (keepSelectionRef.current) event.preventDefault();
                keepSelectionRef.current = false;
              }}
              onClick={event => {
                if (!touch) {
                  if (!listOpen) { openList(); }
                  return;
                }
                if (pickOnly) {
                  if (listOpen) { setListOpen(false); setActive(-1); } else openList();
                  return;
                }
                const input = event.currentTarget;
                const refocusedForKeyboard = keyboardHeld;
                if (keyboardHeld) {
                  // A phone shows its keyboard only on a focus inside the tap, and not for a box already focused.
                  input.inputMode = 'text';
                  setKeyboardHeld(false);
                  refocusingRef.current = true;
                  input.blur();
                  input.focus();
                  refocusingRef.current = false;
                }
                if (enteredByTapRef.current || refocusedForKeyboard) {
                  enteredByTapRef.current = false;
                  // setSelectionRange rather than select(): iPhone Safari keeps a select() made in a tap less reliably.
                  input.setSelectionRange(0, input.value.length);
                }
              }}
              onChange={event => {
                // Without the marks that keep an end-first value's brackets in place (showEnd).
                const typed = event.target.value.replace(/\u200E/g, '');
                const resolved = resolveOption(options, typed);
                setTyping(true);
                setText(typed);
                setFilter(typed);
                if (!typed.trim()) {
                  // Emptied is a choice - none - so nothing is lit for Enter or Tab to take back, and the list
                  // closes rather than sit over the panel's Save (Shaun could not unassign: Enter re-took the
                  // first person, a click on Save landed on the list). The down arrow opens it again.
                  setListOpen(false);
                  setActive(-1);
                  if (liveChange) onChange(null);
                  return;
                }
                setListOpen(true);
                setActive(bestMatch(options, typed, resolved));
                if (resolved != null && liveChange) onChange(resolved);
              }}
              onKeyDown={onKeyDown}
              onBlur={() => {
                if (refocusingRef.current) return;
                setKeyboardHeld(false);
                setTyping(false);
                setListOpen(false);
                setActive(-1);
                if (!liveChange) {
                  // A form takes on leaving what the typing named; blank clears where the field can be cleared.
                  const typed = text.trim();
                  const resolved = typed ? resolveOption(options, typed) : null;
                  if (resolved != null && resolved !== valueId && resolved !== reportedRef.current) { setText(optionName(options, resolved)); report(resolved); return; }
                  if (!typed && clearable && valueId != null && reportedRef.current !== null) { report(null); return; }
                }
                setText(optionName(options, valueId));
              }}
              disabled={disabled}
              className="w-full rounded"
              style={{
                boxSizing: 'border-box',
                height: boxHeight(size),
                padding: '0 30px 0 10px',
                font: 'inherit',
                fontSize: size,
                outline: 'none',
                border: `1px solid ${invalid ? colorScheme.red.foreground : 'transparent'}`,
                backgroundColor: disabled ? colorScheme.default.backgroundDisabled1 : colorScheme.default.background2,
                color: disabled ? colorScheme.default.foregroundDisabled1 : colorScheme.default.foreground,
                textOverflow: 'ellipsis',
                textAlign: 'left'
              }}
            />
            {trailingIcon()}
            </div>
            {!hideLabel && required && valueId == null && (
              <FieldErrorText>{i18next.t('ticket-details.edit-ticket-view.required-field-must-be-filled-out')}</FieldErrorText>
            )}
            {listOpen && position && (
              <TypeaheadList
                listRef={listRef}
                position={position}
                shown={shown}
                active={active}
                selectedId={valueId}
                filter={filter}
                globalTheme={globalTheme}
                onPick={pick}
                onHover={setActive}
                size={size}
              />
            )}
          </div>
        );
      }}
    />
  );
};

interface MultiProps {
  label: string;
  options: CategoryOption[];
  valueIds: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  size?: ListSize;
  /**
   * Offered when what is typed names no option: a last row, "createLabel(name)", that makes the option and adds
   * it. Resolves with the new option (the caller adds it to `options`), or null if it could not be made.
   */
  onCreate?: (name: string) => Promise<CategoryOption | null>;
  createLabel?: (name: string) => string;
  /** The chosen items as chips in name order, wherever they were picked - as the ticket page shows tags. */
  sortChips?: boolean;
  /** Greyed, chips without their X, nothing to type - a closed, merged or deflected ticket on the ticket page. */
  disabled?: boolean;
  /** Leaves the label out, for a form that draws its own. */
  hideLabel?: boolean;
  /** The list as wide as the box and in line with it - a form's dropdowns, as production's are. */
  listFitsBox?: boolean;
}

/** The id of the "create" row a multi box offers - never a real option's. */
const CREATE_ID = -1;

/**
 * Several picks in one typable box, as chips - Collaborators. Typing filters, Enter or a click adds the lit
 * option, a chip's × or Backspace in an empty box removes one.
 */
export const MultiTypeaheadField = ({ label, options, valueIds, onChange, placeholder, size = 14, onCreate, createLabel, sortChips, disabled, hideLabel, listFitsBox }: MultiProps) => {
  const chips = sortChips
    ? [...valueIds].sort((a, b) => optionName(options, a).localeCompare(optionName(options, b)))
    : valueIds;
  const [text, setText] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const available = options.filter(option => !valueIds.includes(option.id));
  /** A name that matches no option, chosen or not, can be made - only where the caller can make one. */
  const creatable = (typed: string) => !!onCreate && !!typed.trim() &&
    !options.some(option => option.name.trim().toLowerCase() === typed.trim().toLowerCase());
  const matches = matchingOptions(available, text);
  const shown = creatable(text) ? [...matches, { id: CREATE_ID, name: (createLabel ?? (name => name))(text.trim()) }] : matches;
  const position = useListPlacement(boxRef, listRef, listOpen, options.map(option => option.name), size, listFitsBox);
  // On a touch screen, as the single box: a tap is for typing, and the arrow opens the list with the keyboard down.
  const touch = useRef(isTouchScreen()).current;
  const [keyboardHeld, setKeyboardHeld] = useState(false);
  const refocusingRef = useRef(false);
  /** Focuses the box again inside the tap, with or without a keyboard - a phone changes it only on a fresh focus. */
  const refocus = (keyboard: boolean) => {
    const input = inputRef.current;
    if (!input) return;
    input.inputMode = keyboard ? 'text' : 'none';
    setKeyboardHeld(!keyboard);
    refocusingRef.current = true;
    if (document.activeElement === input) input.blur();
    input.focus();
    refocusingRef.current = false;
  };

  useEffect(() => {
    if (active >= 0) revealRow(listRef.current, active);
  }, [active]);

  const add = async (option: CategoryOption) => {
    if (option.id === CREATE_ID) {
      if (creating || !onCreate) return;
      setCreating(true);
      const made = await onCreate(text.trim());
      setCreating(false);
      if (!made) return;
      onChange([...valueIds, made.id]);
    } else {
      onChange([...valueIds, option.id]);
    }
    // The list closes and the cursor stays in the box, as on the ticket page: typing, Down or a click opens it again.
    // On a phone too - keeping it open for several taps was tried and dropped (Shaun, 2026-10-22).
    setText('');
    setActive(-1);
    setListOpen(false);
    inputRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (!listOpen) { setListOpen(true); setActive(0); return; }
      if (!shown.length) return;
      setActive(prev => (prev + (event.key === 'ArrowDown' ? 1 : -1) + shown.length) % shown.length);
      return;
    }
    // Tab does what Enter does: takes the lit person and stays, so several can be added in a row. With no one
    // lit, both are left to the panel (Tab moves on - off the last field that saves; Enter saves).
    if ((event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey)) && listOpen && active >= 0 && shown[active]) {
      event.preventDefault();
      event.stopPropagation();
      add(shown[active]);
      return;
    }
    if (event.key === 'Escape' && listOpen) {
      event.preventDefault();
      event.stopPropagation();
      setListOpen(false);
      setActive(-1);
      return;
    }
    if (event.key === 'Backspace' && !text && valueIds.length) {
      onChange(valueIds.filter(id => id !== chips[chips.length - 1]));
    }
  };

  return (
    <ProviderConsumer
      render={globalTheme => {
        const { colorScheme } = globalTheme.siteVariables;
        const textColor = disabled ? colorScheme.default.foregroundDisabled1 : colorScheme.default.foreground;
        return (
          <div>
            {!hideLabel && <Text content={label} style={{ fontSize: 14 }} />}
            <div
              ref={boxRef}
              className="relative w-full rounded flex flex-wrap items-center"
              style={{ boxSizing: 'border-box', minHeight: boxHeight(size), padding: '2px 30px 2px 4px', gap: 4, fontSize: size, backgroundColor: disabled ? colorScheme.default.backgroundDisabled1 : colorScheme.default.background2 }}
              onMouseDown={event => {
                if (disabled || event.target !== boxRef.current) return;
                event.preventDefault();
                if (touch) { refocus(true); return; }
                inputRef.current?.focus();
                setListOpen(true);
              }}
            >
              {chips.map(id => (
                <span
                  key={id}
                  className="inline-flex items-center rounded-full"
                  // --tikit-pill-bg: the lighter grey list pills take in dark mode (main.css). 1px top / 3px bottom, not
                  // 2 / 2: the text sat 0.4px below the chip's middle; as the list's pills, it now sits just above it.
                  style={{ padding: disabled ? '1px 10px 3px 10px' : '1px 4px 3px 10px', gap: 4, backgroundColor: `var(--tikit-pill-bg, ${colorScheme.default.background})`, color: textColor }}
                >
                  {optionName(options, id)}
                  {!disabled && <button
                    type="button"
                    aria-label={`${i18next.t('ticket.ticket-list.configure-column.remove')} ${optionName(options, id)}`}
                    // Out of the Tab order: Tab into the field landed here, and the first space typed pressed it and
                    // took the chip off (measured on My Work). From the keyboard, Backspace in the box removes chips.
                    tabIndex={-1}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => onChange(valueIds.filter(other => other !== id))}
                    style={{ display: 'flex', border: 0, padding: 2, background: 'transparent', cursor: 'pointer', color: colorScheme.default.foreground2 }}
                  >
                    <CloseIcon size="smaller" />
                  </button>}
                </span>
              ))}
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-label={label}
                aria-expanded={listOpen}
                aria-autocomplete="list"
                autoComplete="off"
                spellCheck={false}
                placeholder={valueIds.length ? '' : placeholder}
                value={text}
                // Opens on a click, typing or the arrow keys - not on Tab (see the single box). Not on a tap.
                inputMode={touch && keyboardHeld ? 'none' : undefined}
                onClick={() => {
                  if (!touch) { setListOpen(true); return; }
                  if (keyboardHeld) refocus(true);
                }}
                onChange={event => {
                  const typed = event.target.value;
                  setText(typed);
                  setListOpen(true);
                  const best = bestMatch(available, typed, resolveOption(available, typed));
                  // Nothing matches but a new one can be made: light the create row, so Enter makes it.
                  setActive(best < 0 && creatable(typed) ? matchingOptions(available, typed).length : best);
                }}
                onKeyDown={onKeyDown}
                onBlur={() => {
                  if (refocusingRef.current) return;
                  setKeyboardHeld(false);
                  setListOpen(false);
                  setActive(-1);
                  setText('');
                }}
                disabled={disabled}
                style={{ flex: '1 0 80px', minWidth: 80, height: boxHeight(size) - 6, border: 0, padding: '0 6px', font: 'inherit', fontSize: size, outline: 'none', background: 'transparent', color: textColor }}
              />
              {!disabled && <BoxIcon
                top={boxHeight(size) / 2}
                label={label}
                color={colorScheme.default.foreground}
                onClick={() => {
                  if (listOpen) { setListOpen(false); setActive(-1); return; }
                  if (touch) refocus(false);
                  else if (document.activeElement !== inputRef.current) inputRef.current?.focus();
                  setListOpen(true);
                }}
              >
                <ChevronDownIcon size="small" />
              </BoxIcon>}
            </div>
            {listOpen && position && (
              <TypeaheadList
                listRef={listRef}
                position={position}
                shown={shown}
                active={active}
                selectedId={null}
                filter={text}
                globalTheme={globalTheme}
                onPick={add}
                onHover={setActive}
                size={size}
              />
            )}
          </div>
        );
      }}
    />
  );
};
